import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Heading, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import { RentalManagerListingCriteriaType } from '@zg-rentals/ts-rental-manager-api-beta-client';

import listingCriteriaUtils from '../../../../../../srcLegacy/shared/utils/listingCriteriaUtils';
import { useGetListingDetails, useGetListingRecommendations } from '../../../../../hooks/queries/properties';
import numberUtils from '../../../../../utils/numberUtils';
import stringHelper from '../../../../../utils/stringHelper';

import { NOT_APPLICABLE, NOT_SET } from '../../../../../../srcLegacy/shared/constants/ListingCriteriaConstants';
import { REVIEW_PAGE_SECTIONS } from '../../../../../constants/ListingDetailConstants';
import { RECS } from '../../../../../constants/RecommendationsConstants';
import { SF_MODAL_COMPONENTS } from '../constants';

import AccordionIconError from '../../../../../../srcLegacy/shared/components/WizardModularListing/Review/components/AccordionIconError';
import FieldValueDisplay from '../../../../../../srcLegacy/shared/components/WizardModularListing/Review/components/FieldValueDisplay';
import RecommendationModal from '../../../../modals/RecommendationModal';
import ContentSectionEditButton from '../components/ContentSectionEditButton';

import { iconPlaceholderStyles, reviewSectionStyles, reviewSectionTitleStyles, reviewTitleRowStyles } from '../styles';

import RecommendationIconBulb from '../../../../PostingPath/RecommendationIconBulb';

import type { MouseEvent } from 'react';

const ScreeningCriteriaContent = ({ onEditClick }: { onEditClick: (e: MouseEvent<HTMLButtonElement>) => void }) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData, recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });

  const [displayRecsModal, setDisplayRecsModal] = useState('');
  const recommendationTypes = new Set(recommendationsData?.recommendations.map((r) => r.type) ?? []);

  const currentCriteria = listingDetails?.listingCriteria?.criteria ?? [];
  const petsAllowedDto = listingDetails?.petsAllowed;

  const getCriterion = (type: string) => listingCriteriaUtils.getCriteriaByType(currentCriteria, type);

  const {
    creditScore,
    isCreditScoreEnabled,
    isCreditScoreNegotiable,
    isRentToIncomeRatioEnabled,
    isRentToIncomeRatioNegotiable,
    petCriteria,
    rentToIncomeRatio,
  } = useMemo(() => {
    return {
      creditScore: getCriterion(RentalManagerListingCriteriaType.CREDIT_SCORE_MIN)?.criterion,
      isCreditScoreEnabled: getCriterion(RentalManagerListingCriteriaType.CREDIT_SCORE_MIN)?.isEnabled ?? true,
      isCreditScoreNegotiable: getCriterion(RentalManagerListingCriteriaType.CREDIT_SCORE_MIN)?.negotiable ?? false,
      isRentToIncomeRatioEnabled:
        getCriterion(RentalManagerListingCriteriaType.INCOME_TO_RENT_RATIO)?.isEnabled ?? true,
      isRentToIncomeRatioNegotiable:
        getCriterion(RentalManagerListingCriteriaType.INCOME_TO_RENT_RATIO)?.negotiable ?? false,
      petCriteria: {
        catCount: getCriterion(RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT)?.criterion,
        isCatCountNegotiable: getCriterion(RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT)?.negotiable,
        smallDogCount: getCriterion(RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT)?.criterion,
        isSmallDogCountNegotiable: getCriterion(RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT)
          ?.negotiable,
        largeDogCount: getCriterion(RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT)?.criterion,
        isLargeDogCountNegotiable: getCriterion(RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT)
          ?.negotiable,
        petDeposit: getCriterion(RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT)?.criterion,
        petRent: getCriterion(RentalManagerListingCriteriaType.PET_POLICY_PET_RENT)?.criterion,
        isPetDepositRefundable:
          getCriterion(RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT)?.refundable ?? null,
      },
      rentToIncomeRatio: getCriterion(RentalManagerListingCriteriaType.INCOME_TO_RENT_RATIO)?.criterion,
    };
  }, [currentCriteria]);

  const arePetsAllowedViaDTO =
    petsAllowedDto?.catsDto?.allowed || petsAllowedDto?.smallDogsDto?.allowed || petsAllowedDto?.largeDogsDto?.allowed;

  const isPetPolicyNegotiable =
    petCriteria.isSmallDogCountNegotiable || petCriteria.isLargeDogCountNegotiable || petCriteria.isCatCountNegotiable;

  const financialDisplays = useMemo(() => {
    const isRentToIncomeRatioNegotiableDisplay =
      listingCriteriaUtils.getNegotiableDisplay(isRentToIncomeRatioNegotiable);
    const isCreditScoreNegotiableDisplay = listingCriteriaUtils.getNegotiableDisplay(isCreditScoreNegotiable);

    const getRentToIncomeRatioDisplay = () => {
      let response = rentToIncomeRatio;
      let suffix = `(${isRentToIncomeRatioNegotiableDisplay})`;
      if (
        rentToIncomeRatio === '' ||
        rentToIncomeRatio === NOT_APPLICABLE ||
        rentToIncomeRatio === null ||
        rentToIncomeRatio === undefined
      ) {
        response = NOT_SET;
        suffix = isRentToIncomeRatioNegotiable ? suffix : '';
      }
      return `${response} ${suffix}`;
    };

    const getCreditScoreDisplay = () => {
      let response = creditScore;
      let suffix = `(${isCreditScoreNegotiableDisplay})`;
      if (creditScore === '' || creditScore === NOT_APPLICABLE || creditScore === null || creditScore === undefined) {
        response = NOT_SET;
        suffix = isCreditScoreNegotiable ? suffix : '';
      } else {
        suffix = response === '740+' ? suffix : `or above ${suffix}`;
      }
      return `${response} ${suffix}`;
    };

    return {
      rentToIncomeRatioDisplay: `Income-to-rent ratio: ${getRentToIncomeRatioDisplay()}`,
      creditScoreDisplay: `Credit score: ${getCreditScoreDisplay()}`,
    };
  }, [isRentToIncomeRatioNegotiable, rentToIncomeRatio, isCreditScoreNegotiable, creditScore]);

  const petDisplays = useMemo(() => {
    const isPetCriteriaCatCountZero = petCriteria.catCount === '0';
    const isPetCriteriaSmallDogCountZero = petCriteria.smallDogCount === '0';
    const isPetCriteriaLargeDogCountZero = petCriteria.largeDogCount === '0';

    const areAllPetCriteriaZero =
      isPetCriteriaCatCountZero && isPetCriteriaSmallDogCountZero && isPetCriteriaLargeDogCountZero;

    const areAllPetsAllowedFalse =
      petsAllowedDto?.catsDto?.allowed === false &&
      petsAllowedDto?.smallDogsDto?.allowed === false &&
      petsAllowedDto?.largeDogsDto?.allowed === false;

    const catDisplay =
      petCriteria.catCount != undefined
        ? isPetCriteriaCatCountZero
          ? 'No cats'
          : stringHelper.pluralize(petCriteria.catCount, 'cat', 'cats')
        : petsAllowedDto?.catsDto?.allowed
          ? 'Cats allowed'
          : false;

    const smallDogDisplay =
      petCriteria.smallDogCount != undefined
        ? isPetCriteriaSmallDogCountZero
          ? 'No small dogs'
          : stringHelper.pluralize(
              petCriteria.smallDogCount,
              'small dog under 40 pounds',
              'small dogs under 40 pounds each',
            )
        : petsAllowedDto?.smallDogsDto?.allowed
          ? 'Small dog under 40 pounds'
          : false;

    const largeDogDisplay =
      petCriteria.largeDogCount != undefined
        ? isPetCriteriaLargeDogCountZero
          ? 'No large dogs'
          : stringHelper.pluralize(
              petCriteria.largeDogCount,
              'large dog over 40 pounds',
              'large dogs over 40 pounds each',
            )
        : petsAllowedDto?.largeDogsDto?.allowed
          ? 'Large dog over 40 pounds'
          : false;

    const petDepositDisplay =
      petCriteria.petDeposit === '' || petCriteria.petDeposit === null || petCriteria.petDeposit === undefined
        ? ''
        : `Pet deposit: ${numberUtils.asCurrency(Number(petCriteria.petDeposit))} ${petCriteria.isPetDepositRefundable === false ? '' : '(refundable)'}`;

    const petRentDisplay =
      petCriteria.petRent === '' || petCriteria.petRent === null || petCriteria.petRent === undefined
        ? ''
        : `Pet rent: ${numberUtils.asCurrency(Number(petCriteria.petRent))} pet rent per month`;

    const petPolicyDisplay =
      isPetPolicyNegotiable === null || isPetPolicyNegotiable === undefined
        ? ''
        : `Pet policy is ${isPetPolicyNegotiable ? 'negotiable' : 'non-negotiable'}`;

    return {
      areAllPetCriteriaZero,
      areAllPetsAllowedFalse,
      catDisplay,
      smallDogDisplay,
      largeDogDisplay,
      petDepositDisplay,
      petRentDisplay,
      petPolicyDisplay,
    };
  }, [petsAllowedDto, petCriteria, isPetPolicyNegotiable]);

  const screeningRecs = recommendationsBySection[REVIEW_PAGE_SECTIONS.SCREENING_CRITERIA];

  const renderFinancialExpectationsDisplay = (
    <div data-testid="FinancialExpectationsDisplay">
      {isRentToIncomeRatioEnabled && <Paragraph>{financialDisplays.rentToIncomeRatioDisplay}</Paragraph>}
      {isCreditScoreEnabled && <Paragraph>{financialDisplays.creditScoreDisplay}</Paragraph>}
    </div>
  );

  const renderPetsDisplay = () => {
    const {
      areAllPetCriteriaZero,
      catDisplay,
      smallDogDisplay,
      largeDogDisplay,
      petDepositDisplay,
      petRentDisplay,
      petPolicyDisplay,
    } = petDisplays;

    if (arePetsAllowedViaDTO === null || arePetsAllowedViaDTO === undefined) {
      return (
        <div data-testid="PetsDisplay">
          <Paragraph color="text.subtle">Action needed</Paragraph>
        </div>
      );
    }

    return (
      <div data-testid="PetsDisplay">
        {areAllPetCriteriaZero ? (
          <Paragraph data-testid="NoPetsDisplay">No pets</Paragraph>
        ) : (
          <Box css={{ marginBottom: '200' }} data-testid="PetsDisplayList">
            <Paragraph>{catDisplay}</Paragraph>
            <Paragraph>{smallDogDisplay}</Paragraph>
            <Paragraph>{largeDogDisplay}</Paragraph>
            <Box css={{ marginTop: '200' }}>
              <Paragraph>{petDepositDisplay}</Paragraph>
            </Box>
            <Paragraph>{petRentDisplay}</Paragraph>
          </Box>
        )}
        <Text asChild>
          <p data-testid="PetPolicyDisplay">{petPolicyDisplay}</p>
        </Text>
      </div>
    );
  };

  return (
    <div data-testid="ScreeningCriteriaContent" data-group-title="ScreeningCriteria">
      <Box css={reviewSectionStyles} data-testid="FinancialExpectations">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles} />
            <Heading level={4} css={{ textStyle: 'body-bold' }}>
              Financial expectations
            </Heading>
          </Box>

          <ContentSectionEditButton
            buttonFieldName="financial-expectations"
            value={SF_MODAL_COMPONENTS.FinancialExpectationsFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>

        <FieldValueDisplay value={[renderFinancialExpectationsDisplay]} />
      </Box>

      <Box data-testid="Pets">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {arePetsAllowedViaDTO === null || arePetsAllowedViaDTO === undefined ? (
                <AccordionIconError tabIndex={0} title="Pets incomplete error" />
              ) : screeningRecs?.[RECS.PETS_NOT_ALLOWED] ? (
                <RecommendationIconBulb
                  fieldName="pets"
                  removeFlex={true}
                  onClick={() => setDisplayRecsModal(RECS.PETS_NOT_ALLOWED)}
                />
              ) : null}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Pets
              </Heading>
              {recommendationTypes.has(RECS.PETS_NOT_ALLOWED) && (
                <TextButton
                  aria-label="learn-more-about-pets"
                  textStyle="body"
                  onClick={() => setDisplayRecsModal(RECS.PETS_NOT_ALLOWED)}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            buttonFieldName="pets"
            value={SF_MODAL_COMPONENTS.PetsAllowedFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>

        <FieldValueDisplay value={[renderPetsDisplay()]} />
      </Box>

      {displayRecsModal && (
        <RecommendationModal onClose={() => setDisplayRecsModal('')} recommendationType={displayRecsModal} />
      )}
    </div>
  );
};

export default ScreeningCriteriaContent;
